import { ENTITY_PREFIX } from '../../constants';
import { sessionActions } from '../../store';
import {
  EVV_GEOLOCATION_ERRORS,
  EVV_GEOLOCATION_OPTIONS,
  EVV_TOKEN_KINDS,
  EVV_TOKEN_STATUSES,
} from './constants';

import type { EVVTokenKind, IEVVCaptureGpsPayload, IEVVSideStatus } from '../../utils/types';
import type { CreateEvvTokenParams, EvvGeolocationResult } from './types';

export function formatSessionNoteId(sessionId: number | string | null | undefined): string {
  if (!sessionId) return `${ENTITY_PREFIX.SessionNotes}------`;

  return `${ENTITY_PREFIX.SessionNotes}${String(sessionId).padStart(6, '0')}`;
}

export function isMobileChrome(): boolean {
  if (!globalThis.navigator) return false;

  const userAgent = globalThis.navigator.userAgent;
  return (
    /CriOS|Chrome/i.test(userAgent) &&
    /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent)
  );
}

export function getEvvGeolocation(): Promise<EvvGeolocationResult> {
  if (!globalThis.navigator?.geolocation) {
    return Promise.resolve({ error: EVV_GEOLOCATION_ERRORS.UNSUPPORTED });
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ coords }),
      () => resolve({ error: EVV_GEOLOCATION_ERRORS.FAILED }),
      EVV_GEOLOCATION_OPTIONS,
    );
  });
}

export function buildEvvLocationPayload(coords: GeolocationCoordinates): IEVVCaptureGpsPayload {
  return {
    lat: coords.latitude,
    long: coords.longitude,
    accuracy_meters: coords.accuracy,
    device_info: navigator.userAgent,
  };
}

export function buildEvvVerificationRoute({
  token,
  kind,
  sessionId,
  returnToSession = false,
}: {
  token: string;
  kind: EVVTokenKind;
  sessionId: number | string;
  returnToSession?: boolean;
}): string {
  const searchParams = new URLSearchParams({
    token,
    kind,
    sessionId: String(sessionId),
  });
  if (returnToSession) {
    searchParams.set('returnToSession', 'true');
  }

  return `/evv/verify?${searchParams.toString()}`;
}

export function isEvvSideCaptured(status: IEVVSideStatus | undefined): boolean {
  return Boolean(status?.captured || status?.token_status === EVV_TOKEN_STATUSES.CAPTURED);
}

export function createEvvToken({ kind, ...params }: CreateEvvTokenParams) {
  return kind === EVV_TOKEN_KINDS.START
    ? sessionActions.createStartEvvToken(params)
    : sessionActions.createEndEvvToken(params);
}
